import { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Slider from "react-slick";
import { 
  updateCarListingStatus, 
  uploadInspectReport,
  deleteCar,
  fetchSingleCarListing,
  fetchCarListings,
  updateCarListing 
} from "../../features/carListing/carListingSlice";
import Loader from "../Loader";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../css/car_listing.css";
import { 
  FaCheckCircle, 
  FaChevronLeft, 
  FaChevronRight, 
  FaTimesCircle, 
  FaEdit, 
  FaTrash, 
  FaEye,
  FaArrowLeft 
} from "react-icons/fa";
import { MdPending } from "react-icons/md";
import { Modal, Button, Badge } from "react-bootstrap";
import { isUserAuthorized } from "../../utils/authorize";
import { resolveListingUser } from "../../utils/carUserUtils";

const CarListingDetailLayer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  // One-time hard reload on landing to fix image initialization issues


  const carListings = useSelector((state) => state.carListing?.items);
  const selectedCar = useSelector((state) => state.carListing?.selectedCar);
  const role = useSelector((state) => state?.cred?.user?.role);
  const status = useSelector((state) => state.carListing?.status);
  const error = useSelector((state) => state.carListing?.error);

  const [modalData, setModalData] = useState({ show: false, action: '', id: null });
  const [deleteModalData, setDeleteModalData] = useState({ show: false, id: null });
  const [editModalData, setEditModalData] = useState({ 
    show: false, 
    listing: null,
    formData: {}
  });
  const [pdfFiles, setPdfFiles] = useState({});
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  const listingRoute = useMemo(() => {
    const fromListPath = location.state?.fromListPath;
    if (fromListPath) return fromListPath;

    const filter = location.state?.approvalStatusFilter;
    switch (filter) {
      case "approved":
        return "/car-listing-approved";
      case "rejected":
        return "/car-listing-rejected";
      case "pending":
        return "/car-listing-pending";
      default:
        return "/car-listing-status";
    }
  }, [location.state?.fromListPath, location.state?.approvalStatusFilter]);

  // Find car from list or fetch if needed, and merge userId if available
  const car = useMemo(() => {
    const baseCar = (selectedCar && selectedCar._id === id) 
      ? selectedCar 
      : carListings.find(c => c._id === id);
    if (!baseCar) return null;
    
    // Check if we have a car in carListings with populated userId
    const carWithUserId = carListings.find(c => c._id === id && c.userId && typeof c.userId === 'object');
    
    // If baseCar has userId as just a string/ID, but we have populated userId in carListings, use it
    if (baseCar && carWithUserId && carWithUserId.userId) {
      return {
        ...baseCar,
        userId: carWithUserId.userId
      };
    }
    
    return baseCar;
  }, [selectedCar, carListings, id]);

  const resolvedUser = useMemo(() => resolveListingUser(car || {}), [car]);

  // Format seller comment into readable bullets and line breaks
  const formattedSellerComment = useMemo(() => {
    const raw = car?.sellerComment;
    if (!raw) return "";

    const normalized = String(raw)
      .replace(/^\s*["'“”]+|["'“”]+\s*$/g, "")
      .replace(/\r\n|\r|\n/g, "\n")
      .replace(/[\u2012-\u2015]/g, "-")
      .replace(/\u00A0/g, " ")
      .trim();

    let withSentenceBreaks = normalized.replace(/([.!?])\s+(?=[A-Za-z\u0600-\u06FF])/g, "$1\n");

    withSentenceBreaks = withSentenceBreaks.replace(/(^|\n)\s*[-*•]\s+/g, (match, leading) => `${leading}• `);

    return withSentenceBreaks
      .split("\n")
      .map((line) => line.trimEnd())
      .join("\n");
  }, [car?.sellerComment]);

  useEffect(() => {
    if (id) {
      dispatch(fetchSingleCarListing(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Also fetch car listings if empty, to get populated userId data
    // Fetch with a reasonable limit to find our specific car
    if (carListings.length === 0 && id && status !== 'loading') {
      dispatch(fetchCarListings({ page: 1, limit: 50, status: 'all' }));
    }
  }, [dispatch, carListings.length, id, status]);

  const openModal = (action, listingId) => {
    setModalData({ show: true, action, id: listingId });
  };
  
  const closeModal = () => {
    setModalData({ show: false, action: '', id: null });
  };

  const handleBackToListings = () => {
    setIsNavigatingBack(true);
    navigate(listingRoute, { state: { preserveScroll: true } });
  };

  const confirmModalAction = async () => {
    const { action, id: listingId } = modalData;
    if (action === "approve") {
      await dispatch(updateCarListingStatus({
        id: listingId,
        status: 'approved'
      }));
      toast.success("Car listing approved successfully");
      navigate(listingRoute, { state: { preserveScroll: true } });
    }
    else if (action === "reject") {
      await dispatch(updateCarListingStatus({
        id: listingId,
        status: 'rejected'
      }));
      toast.success("Car listing rejected successfully");
      navigate(listingRoute, { state: { preserveScroll: true } });
    }
    
    // if (action === "approve") {
    //   await dispatch(updateCarListingStatus({ id: listingId, action: { approvalStatus: 'approved' } }));
    //   toast.success("Car listing approved successfully");
    //   navigate(listingRoute, { state: { preserveScroll: true } });
    // } else if (action === "reject") {
    //   await dispatch(updateCarListingStatus({ id: listingId, action: { approvalStatus: 'rejected' } }));
    //   toast.success("Car listing rejected successfully");
    //   navigate(listingRoute, { state: { preserveScroll: true } });
    // }
    closeModal();
  };

  const openDeleteModal = (listingId) => {
    // Use the ID from URL params as fallback if listingId is not provided or invalid
    const carId = listingId || id || car?._id;
    if (!carId) {
      toast.error("Unable to identify car listing. Please refresh the page.");
      return;
    }
    setDeleteModalData({ show: true, id: carId });
  };

  const closeDeleteModal = () => {
    setDeleteModalData({ show: false, id: null });
  };

  const handleDelete = async () => {
    // Always use URL id as primary source, with fallbacks
    const listingId = id || deleteModalData.id || car?._id;
    
    if (!listingId) {
      toast.error("Unable to identify car listing. Please refresh the page.");
      closeDeleteModal();
      return;
    }

    try {
      const result = await dispatch(deleteCar(listingId));
      
      // Check if the action was rejected
      if (deleteCar.rejected.match(result)) {
        const errorMessage = result.error?.message || result.payload?.message || 'Failed to delete car listing';
        toast.error(`Error: ${errorMessage}`);
        return;
      }

      const { payload } = result;
      if (payload?.success !== false) {
        toast.success("Car listing deleted successfully");
        closeDeleteModal();
        navigate(listingRoute, { state: { preserveScroll: true } });
      } else {
        toast.error(payload?.message || "Failed to delete car listing.");
      }
    } catch (err) {
      toast.error(`Error: ${err?.message || err?.toString() || 'An unexpected error occurred'}`);
    }
  };

  const openEditModal = () => {
    if (!car) return;
    setEditModalData({ 
      show: true, 
      listing: car,
      formData: {
        listingTitle: car.title || '',
        askingPrice: car.askingPrice || car.price || '',
        year: car.year || '',
        make: car.make || '',
        model: car.model || '',
        condition: car.condition || '',
        mileage: car.mileage || '',
        primaryDamage: car.primaryDamage || '',
        repairingCost: car.repairingCost || '',
        sellerComment: car.sellerComment || ''
      }
    });
  };

  const closeEditModal = () => {
    setEditModalData({ show: false, listing: null, formData: {} });
  };

  const handleEditSubmit = async () => {
    const { listing, formData } = editModalData;
    if (!listing || !listing?._id) return;

    try {
      const result = await dispatch(updateCarListing({ 
        id: listing?._id, 
        updatedData: {...formData, title: formData.listingTitle, price: formData.askingPrice}
      }));
      
      // Check if the action was rejected
      if (updateCarListing.rejected.match(result)) {
        const errorMessage = result.error?.message || result.payload?.message || 'Failed to update car listing';
        toast.error(`Error updating car listing: ${errorMessage}`);
        return;
      }
      
      toast.success("Car listing updated successfully");
      closeEditModal();
      // Refresh the car data
      if (id) {
        dispatch(fetchSingleCarListing(id));
      }
    } catch (err) {
      // Catch any unexpected errors
      const errorMessage = err?.message || err?.toString() || 'An unexpected error occurred';
      toast.error(`Error updating car listing: ${errorMessage}`);
    }
  };

  const handlePdfUpload = async (listingId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error("Please select a valid PDF file");
      return;
    }

    try {
      setPdfFiles(prev => ({
        ...prev,
        [listingId]: {
          file: file,
          name: file.name,
          url: URL.createObjectURL(file),
          uploading: true
        }
      }));

      const result = await dispatch(uploadInspectReport({ 
        listingId, 
        file 
      })).unwrap();

      setPdfFiles(prev => ({
        ...prev,
        [listingId]: {
          file: file,
          name: result.pdfName,
          url: result.pdfUrl,
          uploading: false
        }
      }));

      toast.success("Inspect report uploaded successfully");
      // Refresh the car data
      if (id) {
        dispatch(fetchSingleCarListing(id));
      }
    } catch (error) {
      setPdfFiles(prev => {
        const newPdfs = { ...prev };
        delete newPdfs[listingId];
        return newPdfs;
      });
      toast.error(`Upload failed: ${error.message}`);
    }
  };

  const hasPdfUploaded = (listingId) => {
    if (pdfFiles[listingId] && pdfFiles[listingId].file) {
      return true;
    }
    const listing = car || carListings.find(c => c._id === listingId);
    return listing && (listing.inspectReportUrl || listing.inspectionReportLink);
  };

  const getStatusBadge = (approvalStatus) => {
    switch (approvalStatus) {
      case 'approved':
        return <Badge bg="success" className="d-flex align-items-center gap-1"><FaCheckCircle size={12} /> Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger" className="d-flex align-items-center gap-1"><FaTimesCircle size={12} /> Rejected</Badge>;
      default:
        return <Badge bg="warning" className="d-flex align-items-center gap-1"><MdPending size={12} /> Pending</Badge>;
    }
  };

  const NextArrow = ({ onClick }) => (
    <div className="custom-arrow next" onClick={onClick}>
      <FaChevronRight />
    </div>
  );

  const PrevArrow = ({ onClick }) => (
    <div className="custom-arrow prev" onClick={onClick}>
      <FaChevronLeft />
    </div>
  );

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    pauseOnHover: true,
    fade: true
  };

  if (status === "loading" && !car) return <Loader />;
  if (status === "failed" && !car) return <p>{error}</p>;
  if (!car) return <p>Car listing not found.</p>;
  if (isNavigatingBack) return <Loader />;

  const userEmail = resolvedUser.email || car?.userId?.email || "";
  const userName = resolvedUser.name || car?.userId?.username || car?.userId?.name || "";
  const userAvatar = resolvedUser.avatar || car?.userId?.avatar || "";

  return (
    <div className="car-listing-detail-container">
      {/* Back Button */}
      <div className="mb-4">
        <Button 
          variant="outline-secondary" 
          onClick={handleBackToListings}
          className="d-flex align-items-center gap-2"
          disabled={isNavigatingBack}
        >
          <FaArrowLeft /> Back to Listings
        </Button>
      </div>

      <div className="row">
        {/* Left Column - Images */}
        <div className="col-lg-6 mb-4">
          <div className="car-image-container-detail">
            {car.images && car.images.length > 0 && (
              <Slider {...carouselSettings}>
                {car.images.map((img, i) => (
                  <div key={i}>
                    <img
                      src={img.url}
                      alt={`Slide ${i + 1}`}
                      className="car-image-detail"
                    />
                  </div>
                ))}
              </Slider>
            )}
            {car.img && car.img.length > 0 && (
              <Slider {...carouselSettings}>
                {car.img.map((img, i) => (
                  <div key={i}>
                    <img
                      src={img.url || img.src}
                      alt={`Slide ${i + 1}`}
                      className="car-image-detail"
                    />
                  </div>
                ))}
              </Slider>
            )}
            {(!car.images || car.images.length === 0) && (!car.img || car.img.length === 0) && (
              <div className="car-image-placeholder">
                <p>No images available</p>
              </div>
            )}
            
            <div className="status-badge-overlay-detail">
              {getStatusBadge(car.approvalStatus)}
            </div>
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="col-lg-6">
          <div className="car-detail-card">
            {/* Header */}
            <div className="car-detail-header d-flex flex-row justify-content-between align-items-center">
              <h2 className="car-detail-title text-capitalize text-start text-lg">{car.listingTitle || car.title}</h2>
              <div className="price-tag-detail">
                <span>{Number(car.askingPrice || car.price).toLocaleString()} AED</span>
              </div>
            </div>

            {/* User Info */}
            <div className="user-info-section-detail">
              <div className="user-avatar-detail">
                <img 
                  src={userAvatar || "/default-avatar.png"}
                  alt="User Avatar"
                  className="avatar-image-detail"
                />
              </div>
              <div className="user-details-detail">
                <p className="user-email-detail">{userEmail || "No Email"}</p>
                <p className="user-name-detail">{userName || "No Name"}</p>
              </div>
            </div>

            {/* Car Details Grid */}
            <div className="car-details-grid">
              <div className="detail-item">
                <span className="detail-label">Year:</span>
                <span className="detail-value">{car.year}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Make:</span>
                <span className="detail-value">{car.make}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Model:</span>
                <span className="detail-value">{car.model || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Condition:</span>
                <span className="detail-value">{car.condition}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Mileage:</span>
                <span className="detail-value">{car.mileage?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Primary Damage:</span>
                <span className="detail-value">{car.primaryDamage || 'N/A'}</span>
              </div>
              {car?.repairingCost && (
                <div className="detail-item">
                  <span className="detail-label">Repairing Cost:</span>
                  <span className="detail-value">{car.repairingCost} AED</span>
                </div>
              )}
            </div>

            {/* Features */}
            {car.features && car.features.filter(f => f.selected && f.selected.length > 0).length > 0 && (
              <div className="features-section-detail">
                <h5 className="features-title-detail">Features</h5>
                <div className="features-list-detail">
                  {car.features
                    .filter(f => f.selected && f.selected.length > 0)
                    .map((f, i) => (
                      <div key={i} className="feature-item-detail">
                        <span className="feature-dot-detail"></span>
                        <div className="feature-content-detail">
                          <span className="feature-name-detail">{f.title}: </span>
                          <span className="feature-values-detail">&nbsp;{f.selected.join(", ")}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Seller Comment */}
            {car.sellerComment && (
              <div className="comment-section-detail">
                <h5 className="comment-title-detail">Seller Comment</h5>
                <p className="comment-text-detail" style={{ whiteSpace: 'pre-line' }}>
                  {formattedSellerComment}
                </p>
              </div>
            )}

            {/* PDF Upload Section */}
            <div className="pdf-upload-section-detail">
              <div className="pdf-upload-header-detail">
                <h5 className="pdf-title-detail">Inspection Report</h5>
                <span className="pdf-required-detail">*Required</span>
              </div>
              
              {!hasPdfUploaded(car._id) ? (
                <div className="pdf-upload-area-detail">
                  <input
                    type="file"
                    id={`pdf-upload-detail-${car._id}`}
                    accept=".pdf"
                    onChange={(e) => handlePdfUpload(car._id, e)}
                    className="pdf-input-detail"
                    disabled={pdfFiles[car._id]?.uploading}
                  />
                  <label htmlFor={`pdf-upload-detail-${car._id}`} className={`pdf-upload-label-detail ${pdfFiles[car._id]?.uploading ? 'uploading' : ''}`}>
                    <div className="pdf-upload-icon-detail">
                      {pdfFiles[car._id]?.uploading ? (
                        <div className="spinner"></div>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14,2 14,8 20,8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                        </svg>
                      )}
                    </div>
                    <div className="pdf-upload-text-detail">
                      <span className="pdf-upload-main-detail">
                        {pdfFiles[car._id]?.uploading ? 'Uploading...' : 'Upload Inspect Report'}
                      </span>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="pdf-uploaded-detail">
                  <div className="pdf-file-info-detail">
                    <div className="pdf-file-icon-detail">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14,2 14,8 20,8"></polyline>
                      </svg>
                    </div>
                    <div className="pdf-file-details-detail">
                      <span className="pdf-file-name-detail">
                        {pdfFiles[car._id]?.name || car.inspectReportName || (car.inspectionReportLink && car.inspectionReportLink.split('/').pop()) || 'Inspect Report'}
                      </span>
                    </div>
                  </div>
                  <div className="pdf-file-actions-detail">
                    <a 
                      href={pdfFiles[car._id]?.url || car.inspectReportUrl || car.inspectionReportLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="pdf-view-btn-detail"
                      title="View PDF"
                    >
                      <FaEye size={14} /> View
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {(role === 'admin' || isUserAuthorized) && (
              <div className="action-buttons-detail">
                <button 
                  className="btn btn-approve-detail" 
                  onClick={() => openModal('approve', car._id)}
                  disabled={car.approvalStatus === 'approved' || !hasPdfUploaded(car._id)}
                  title={!hasPdfUploaded(car._id) ? 'PDF document required for approval' : ''}
                >
                  <FaCheckCircle /> Approve
                </button>
                <button 
                  className="btn btn-reject-detail" 
                  onClick={() => openModal('reject', car._id)}
                  disabled={car.approvalStatus === 'rejected'}
                >
                  <FaTimesCircle /> Reject
                </button>
                {isUserAuthorized(role) && <button 
                  className="btn btn-edit-detail" 
                  onClick={openEditModal}
                >
                  <FaEdit /> Edit
                </button>}
                {isUserAuthorized(role) && <button 
                  className="btn btn-delete-detail" 
                  onClick={() => openDeleteModal(id || car?._id)}
                  disabled={!id && !car?._id}
                >
                  <FaTrash /> Delete
                </button>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      <Modal show={modalData.show} onHide={closeModal} centered className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalData.action === 'approve' ? 'Approve Listing' : 'Reject Listing'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-content d-flex flex-row align-items-center gap-2 p-3">
            <p>Are you sure you want to {modalData.action} this car listing?</p>
            <div className="modal-icon">
              {modalData.action === 'approve' ? (
                <FaCheckCircle size={48} color="#28a745" />
              ) : (
                <FaTimesCircle size={48} color="#dc3545" />
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button
            variant={modalData.action === 'approve' ? 'success' : 'danger'}
            onClick={confirmModalAction}
          >
            Yes, {modalData.action}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={deleteModalData.show} onHide={closeDeleteModal} centered className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete Car Listing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-content d-flex flex-row align-items-center gap-2 p-3">
            <p>Are you sure you want to delete this car listing? This action cannot be undone.</p>
            <div className="modal-icon">
              <FaTrash size={30} color="#dc3545" />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={editModalData.show} onHide={closeEditModal} centered size="lg" className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>Edit Car Listing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="edit-form-container">
            <div className="mb-3">
              <label className="form-label">Listing Title</label>
              <input
                type="text"
                className="form-control"
                value={editModalData.formData.listingTitle || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, listingTitle: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Year</label>
              <input
                type="number"
                className="form-control"
                value={editModalData.formData.year || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, year: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Make</label>
              <input
                type="text"
                className="form-control"
                value={editModalData.formData.make || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, make: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Model</label>
              <input
                type="text"
                className="form-control"
                value={editModalData.formData.model || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, model: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Condition</label>
              <input
                type="text"
                className="form-control"
                value={editModalData.formData.condition || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, condition: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Mileage</label>
              <input
                type="string"
                className="form-control"
                value={editModalData.formData.mileage?.toLocaleString() || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, mileage: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Asking Price</label>
              <input
                type="number"
                className="form-control"
                value={editModalData.formData.askingPrice || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, askingPrice: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Primary Damage</label>
              <input
                type="text"
                className="form-control"
                value={editModalData.formData.primaryDamage || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, primaryDamage: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Repairing Cost (AED)</label>
              <input
                type="number"
                className="form-control"
                value={editModalData.formData.repairingCost || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, repairingCost: e.target.value }
                }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Seller Comment</label>
              <textarea
                className="form-control"
                rows="3"
                value={editModalData.formData.sellerComment || ''}
                onChange={(e) => setEditModalData(prev => ({
                  ...prev,
                  formData: { ...prev.formData, sellerComment: e.target.value }
                }))}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal}>Cancel</Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <style jsx="true">{`
        .car-listing-detail-container {
          padding: 20px 0;
        }

        .car-image-container-detail {
          position: relative;
          height: 500px;
          overflow: hidden;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .car-image-detail {
          width: 100%;
          height: 500px;
          object-fit: cover;
        }

        .car-image-placeholder {
          width: 100%;
          height: 500px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border-radius: 16px;
        }

        .status-badge-overlay-detail {
          position: absolute;
          top: 16px;
          right: 16px;
          z-index: 1;
        }

        .car-detail-card {
          background: #fff;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .car-detail-header {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f0f0f0;
        }

        .car-detail-title {
          font-size: 28px;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 12px;
        }

        .price-tag-detail {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          border-radius: 25px;
          font-weight: 700;
          font-size: 20px;
        }

        .user-info-section-detail {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid #f0f0f0;
          margin-bottom: 24px;
        }

        .user-avatar-detail {
          flex-shrink: 0;
        }

        .avatar-image-detail {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #e9ecef;
        }

        .user-details-detail {
          flex: 1;
        }

        .user-email-detail {
          margin: 0;
          font-size: 16px;
          color: #6c757d;
          font-weight: 600;
        }

        .user-name-detail {
          margin: 4px 0 0 0;
          font-size: 14px;
          color: #adb5bd;
        }

        .car-details-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .detail-label {
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }

        .detail-value {
          color: #212529;
          font-size: 16px;
          font-weight: 500;
        }

        .features-section-detail {
          margin-bottom: 24px;
        }

        .features-title-detail {
          font-size: 18px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
        }

        .features-list-detail {
          max-height: 200px;
          overflow-y: auto;
        }

        .feature-item-detail {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 8px;
        }

        .feature-dot-detail {
          width: 8px;
          height: 8px;
          background: #007bff;
          border-radius: 50%;
          margin-top: 8px;
          flex-shrink: 0;
        }

        .feature-content-detail {
          flex: 1;
          font-size: 14px;
        }

        .feature-name-detail {
          font-weight: 600;
          color: #495057;
        }

        .feature-values-detail {
          color: #6c757d;
        }

        .comment-section-detail {
          margin-bottom: 24px;
          padding: 16px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .comment-title-detail {
          font-size: 18px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
        }

        .comment-text-detail {
          font-size: 14px;
          color: #6c757d;
          font-style: italic;
          margin: 0;
          line-height: 1.6;
        }

        .pdf-upload-section-detail {
          margin-bottom: 24px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .pdf-upload-header-detail {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .pdf-title-detail {
          font-size: 18px;
          font-weight: 600;
          color: #495057;
          margin: 0;
        }

        .pdf-required-detail {
          font-size: 14px;
          color: #dc3545;
          font-weight: 500;
        }

        .pdf-input-detail {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        .pdf-upload-label-detail {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 30px;
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          background: #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
        }

        .pdf-upload-label-detail:hover:not(.uploading) {
          border-color: #007bff;
          background: #f8f9ff;
        }

        .pdf-upload-label-detail.uploading {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .pdf-upload-icon-detail {
          color: #6c757d;
          margin-bottom: 12px;
        }

        .pdf-upload-text-detail {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .pdf-upload-main-detail {
          font-size: 16px;
          font-weight: 600;
          color: #495057;
        }

        .pdf-uploaded-detail {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          border: 1px solid #d4edda;
          border-radius: 8px;
        }

        .pdf-file-info-detail {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .pdf-file-icon-detail {
          color: #28a745;
          flex-shrink: 0;
        }

        .pdf-file-details-detail {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .pdf-file-name-detail {
          font-size: 14px;
          font-weight: 600;
          color: #155724;
          word-break: break-all;
        }

        .pdf-file-actions-detail {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }

        .pdf-view-btn-detail {
          padding: 8px 16px;
          border-radius: 6px;
          border: none;
          background: #007bff;
          color: white;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .pdf-view-btn-detail:hover {
          background: #0056b3;
          transform: scale(1.05);
        }

        .action-buttons-detail {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 24px;
        }

        .action-buttons-detail .btn {
          flex: 1;
          min-width: 120px;
          font-size: 14px;
          padding: 12px 16px;
          border-radius: 8px;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.2s ease;
          border: none;
        }

        .btn-approve-detail {
          background: #28a745;
          color: white;
        }

        .btn-approve-detail:hover:not(:disabled) {
          background: #218838;
        }

        .btn-reject-detail {
          background: #dc3545;
          color: white;
        }

        .btn-reject-detail:hover:not(:disabled) {
          background: #c82333;
        }

        .btn-edit-detail {
          background: #007bff;
          color: white;
        }

        .btn-edit-detail:hover {
          background: #0056b3;
        }

        .btn-update-price-detail {
          background: #ffc107;
          color: #212529;
        }

        .btn-update-price-detail:hover {
          background: #e0a800;
        }

        .btn-delete-detail {
          background: #dc3545;
          color: white;
        }

        .btn-delete-detail:hover {
          background: #c82333;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .custom-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 1;
          background: rgba(0, 0, 0, 0.6);
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          cursor: pointer;
          color: #fff;
          transition: all 0.2s ease;
        }

        .custom-arrow:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }

        .custom-arrow.next {
          right: 16px;
        }

        .custom-arrow.prev {
          left: 16px;
        }

        .slick-dots {
          bottom: 20px;
        }

        .edit-form-container {
          padding: 10px 0;
        }

        @media (max-width: 768px) {
          .car-details-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons-detail {
            flex-direction: column;
          }

          .action-buttons-detail .btn {
            width: 100%;
          }

          .car-image-container-detail {
            height: 300px;
          }

          .car-image-detail {
            height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default CarListingDetailLayer;

